// @flow

import type {Page} from 'types';

import {useCallback} from 'react';
import {useDispatch} from 'react-redux';

import {
  addPaymentInfoEvent,
  basicCheckout,
  bdvSplit,
  callToBookEvent,
  copyVoucherEvent,
  customerIsTravelerEvent,
  leads,
  ecommerceEvent,
  exitLayerEvent,
  inputErrorEvent,
  insuranceConfirmEvent,
  pageLoadEvent,
  pageViewEvent,
  partnerRatingEvent,
  pictureGalleryEvent,
  psd2AuthEvent,
  userVoucherShownEvent,
  voucherEvent,
} from 'redux/middleware/googleTagManager';
import {
  EVENT_ADD_PAYMENT_INFO,
  EVENT_BDV_SPLIT,
  EVENT_CALL_TO_BOOK,
  EVENT_CARRENTAL_CLICK,
  EVENT_COPY_VOUCHER,
  EVENT_CUSTOMER_IS_TRAVELER,
  EVENT_ECOMMERCE,
  EVENT_EXITLAYER_CLICK,
  EVENT_EXITLAYER_LOAD,
  EVENT_INPUT_ERROR,
  EVENT_INSURANCE_CONFIRM,
  EVENT_LEADS,
  EVENT_PARTNER_RATING,
  EVENT_PICTURE_GALLERY_CLICK,
  EVENT_PSD2_AUTH,
  EVENT_SHOWN_VOUCHER,
  EVENT_SOI_CHANGED,
  EVENT_TRANSFER_CLICK,
  EVENT_TRANSFER_LOADED,
  EVENT_VOUCHER,
} from 'redux/middleware/googleTagManager/actionTypes';

const eventsMap = {
  [EVENT_ECOMMERCE]: ecommerceEvent,
  [EVENT_ADD_PAYMENT_INFO]: addPaymentInfoEvent,
  [EVENT_CALL_TO_BOOK]: callToBookEvent,
  [EVENT_CUSTOMER_IS_TRAVELER]: customerIsTravelerEvent,
  [EVENT_EXITLAYER_LOAD]: exitLayerEvent,
  [EVENT_EXITLAYER_CLICK]: exitLayerEvent,
  [EVENT_VOUCHER]: voucherEvent,
  [EVENT_INPUT_ERROR]: inputErrorEvent,
  [EVENT_PARTNER_RATING]: partnerRatingEvent,
  [EVENT_PSD2_AUTH]: psd2AuthEvent,
  [EVENT_INSURANCE_CONFIRM]: insuranceConfirmEvent,
  [EVENT_CARRENTAL_CLICK]: basicCheckout,
  [EVENT_TRANSFER_CLICK]: basicCheckout,
  [EVENT_LEADS]: leads,
  [EVENT_TRANSFER_LOADED]: basicCheckout,
  [EVENT_SOI_CHANGED]: basicCheckout,
  [EVENT_BDV_SPLIT]: bdvSplit,
  [EVENT_COPY_VOUCHER]: copyVoucherEvent,
  [EVENT_SHOWN_VOUCHER]: userVoucherShownEvent,
  [EVENT_PICTURE_GALLERY_CLICK]: pictureGalleryEvent,
};

type TrackingEvent =
  | typeof EVENT_ECOMMERCE
  | typeof EVENT_CALL_TO_BOOK
  | typeof EVENT_ADD_PAYMENT_INFO
  | typeof EVENT_EXITLAYER_LOAD
  | typeof EVENT_EXITLAYER_CLICK
  | typeof EVENT_VOUCHER
  | typeof EVENT_INPUT_ERROR
  | typeof EVENT_PSD2_AUTH
  | typeof EVENT_CARRENTAL_CLICK
  | typeof EVENT_TRANSFER_CLICK
  | typeof EVENT_TRANSFER_LOADED
  | typeof EVENT_INSURANCE_CONFIRM
  | typeof EVENT_BDV_SPLIT
  | typeof EVENT_COPY_VOUCHER
  | typeof EVENT_LEADS
  | typeof EVENT_SHOWN_VOUCHER
  | typeof EVENT_SOI_CHANGED
  | typeof EVENT_PICTURE_GALLERY_CLICK
  | typeof EVENT_CUSTOMER_IS_TRAVELER;

type Tracking = {
  trackPageLoad: (page: Page, pageLoad: boolean) => void,
  trackPageView: (page: Page, pageLoad?: boolean) => void,
  trackEvent: (name: TrackingEvent, params: any) => void,
};

export const useTracking = (): Tracking => {
  const dispatch = useDispatch();

  const trackPageLoad = useCallback(
    (page, pageLoad) => {
      dispatch(pageLoadEvent.action({page, pageLoad}));
    },
    [dispatch]
  );

  const trackPageView = useCallback(
    (page, pageLoad) => {
      dispatch(pageViewEvent.action({page, pageLoad}));
    },
    [dispatch]
  );

  const trackEvent = useCallback(
    (name, params) => {
      if (typeof eventsMap[name]?.action === 'function') {
        dispatch(eventsMap[name].action(params));
      }
    },
    [dispatch]
  );

  return {
    trackPageLoad,
    trackPageView,
    trackEvent,
  };
};
